import React from 'react';
import { TaskStatusDefinition } from '../types/Task';

export type EditableStatusDraft = {
  id: string;
  name: string;
  code: string;
  color: string;
  displayOrder: number;
  isDefault: boolean;
  isCompleted: boolean;
  requiresAssignee: boolean;
  transitionToCodes: string[];
};

export type NewStatusDraft = Omit<EditableStatusDraft, 'id'>;

export type StatusUpdatePayload = {
  name?: string;
  code?: string;
  color?: string;
  displayOrder?: number;
  isDefault?: boolean;
  isCompleted?: boolean;
  requiresAssignee?: boolean;
  transitionToCodes?: string[];
};

export type StatusCreatePayload = {
  name: string;
  code?: string;
  color: string;
  displayOrder?: number;
  isDefault: boolean;
  isCompleted: boolean;
  requiresAssignee: boolean;
  transitionToCodes: string[];
};

export type StatusValidationState = {
  draftIssues: Record<string, { duplicateCode: boolean; effectiveCode: string }>;
  newDraft: { duplicateCode: boolean; effectiveCode: string };
  hasNoDefault: boolean;
  hasMultipleDefaults: boolean;
};

export type StatusAutosaveResult = 'saved' | 'error';
export type StatusDropPlacement = 'above' | 'below';

export const STATUS_AUTOSAVE_DELAY_MS = 900;
export const DEFAULT_STATUS_COLOR = '#6b778c';

export const normalizeStatusDraftOrders = (drafts: EditableStatusDraft[]): EditableStatusDraft[] => {
  return drafts.map((draft, index) => ({ ...draft, displayOrder: index }));
};

const normalizeStatusCodeValue = (value: string): string => value.trim().toLowerCase();

const buildSuggestedStatusCode = (value: string): string => value
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

export const createEmptyNewStatusDraft = (displayOrder: number): NewStatusDraft => ({
  name: '',
  code: '',
  color: DEFAULT_STATUS_COLOR,
  displayOrder,
  isDefault: false,
  isCompleted: false,
  requiresAssignee: false,
  transitionToCodes: [],
});

export const mapDefinitionToStatusDraft = (status: TaskStatusDefinition): EditableStatusDraft => ({
  id: status.id,
  name: status.name,
  code: status.code,
  color: status.color,
  displayOrder: status.displayOrder,
  isDefault: status.isDefault,
  isCompleted: status.isCompleted,
  requiresAssignee: status.requiresAssignee,
  transitionToCodes: [...status.transitionToCodes],
});

export const cloneStatusDraft = (draft: EditableStatusDraft): EditableStatusDraft => ({
  ...draft,
  transitionToCodes: [...draft.transitionToCodes],
});

export const mapDraftToStatusDefinition = (draft: EditableStatusDraft): TaskStatusDefinition => ({
  id: draft.id,
  name: draft.name,
  code: draft.code,
  color: draft.color,
  displayOrder: draft.displayOrder,
  isDefault: draft.isDefault,
  isCompleted: draft.isCompleted,
  requiresAssignee: draft.requiresAssignee,
  transitionToCodes: [...draft.transitionToCodes],
});

export const createSavedStatusDraftMap = (drafts: EditableStatusDraft[]): Record<string, EditableStatusDraft> => {
  return drafts.reduce<Record<string, EditableStatusDraft>>((acc, draft) => {
    acc[draft.id] = cloneStatusDraft(draft);
    return acc;
  }, {});
};

export const buildStatusUpdatePayload = (draft: EditableStatusDraft): StatusUpdatePayload => ({
  name: draft.name.trim(),
  code: draft.code.trim() || undefined,
  color: draft.color,
  displayOrder: draft.displayOrder,
  isDefault: draft.isDefault,
  isCompleted: draft.isCompleted,
  requiresAssignee: draft.requiresAssignee,
  transitionToCodes: [...draft.transitionToCodes],
});

export const buildStatusCreatePayload = (draft: NewStatusDraft): StatusCreatePayload => ({
  ...draft,
  name: draft.name.trim(),
  code: draft.code.trim() || undefined,
  transitionToCodes: [...draft.transitionToCodes],
});

export const getEffectiveStatusCode = (draft: Pick<EditableStatusDraft, 'name' | 'code'>): string => {
  return normalizeStatusCodeValue(draft.code) || buildSuggestedStatusCode(draft.name);
};

export const serializeStatusDraft = (draft: EditableStatusDraft): string => JSON.stringify({
  name: draft.name.trim(),
  code: draft.code.trim(),
  color: draft.color,
  displayOrder: draft.displayOrder,
  isDefault: draft.isDefault,
  isCompleted: draft.isCompleted,
  requiresAssignee: draft.requiresAssignee,
  transitionToCodes: [...draft.transitionToCodes].sort(),
});

export const getStatusBadgeStyle = (color?: string): React.CSSProperties | undefined => {
  if (!color || !color.startsWith('#') || (color.length !== 7 && color.length !== 4)) {
    return undefined;
  }

  const resolvedColor = color.length === 4
    ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
    : color;

  return {
    color: resolvedColor,
    borderColor: `${resolvedColor}33`,
    backgroundColor: `${resolvedColor}14`,
  };
};